import { getHeaders, PURCHASER_API_BASE_URL } from "./Constants";
import { z } from "zod";

// The most common API error response
const APIErrorSchema = z.object({ err: z.string() });
type APIError = z.infer<typeof APIErrorSchema>;

type FetcherOptions = Omit<RequestInit, "body"> & {
    body?: unknown;
};

const DEFAULT_PUBLIC_ERROR_MESSAGE = "An unexpected error occurred.";

class FetcherError extends Error {
    status: number;
    publicMessage?: string;
    body?: string;
    error?: Error;
    apiError?: APIError;

    constructor(message: string, options: { status: number; publicMessage?: string; body?: string; error?: Error; apiError?: APIError }) {
        super(message);
        this.name = "FetcherError";
        this.status = options.status;
        this.publicMessage = options.publicMessage ?? DEFAULT_PUBLIC_ERROR_MESSAGE;
        this.body = options.body;
        this.error = options.error;
        this.apiError = options.apiError;
    }

    // This is called when the obect is used in JSON.stringify(), so this just gives a clean output rather than outputting some weird version of the object
    toJSON() {
        const { status, publicMessage, body, error, apiError } = this;
        return {
            status,
            publicMessage,
            body,
            error: error
                ? {
                      name: error.name,
                      message: error.message,
                      cause: error.cause,
                      stack: error.stack,
                  }
                : undefined,
            apiError,
        };
    }
}

class Fetcher {
    // Constructor to make a custom instance with a different baseUrl and headers
    constructor(private baseUrl: string) {}

    // The default instance that is used for Purchaser API calls
    static defaultInstance: Fetcher | null = null;

    // Initialize or get the default instance if already initialized
    static default(): Fetcher {
        if (this.defaultInstance) return this.defaultInstance;
        const instance = new Fetcher(PURCHASER_API_BASE_URL);
        this.defaultInstance = instance;
        return instance;
    }

    // Utility for parsing the body from a response to our API error if we got one
    private parseApiError(bodyText: string): APIError | undefined {
        try {
            const parsed = APIErrorSchema.safeParse(JSON.parse(bodyText));
            return parsed.success ? parsed.data : undefined;
        } catch {
            return undefined;
        }
    }

    async rawRequest<T = any>(path: string, options: FetcherOptions = {}): Promise<{ status: number; data: T }> {
        const { body, ...fetchOptions } = options;

        let jsonBody: string | undefined = undefined;
        if (body) {
            // If a body was passed in make sure it's either already stringified or stringify it now
            jsonBody = typeof body === "string" ? body : JSON.stringify(body);
        }

        let response: Response;

        try {
            response = await fetch(`${this.baseUrl}${path}`, {
                ...fetchOptions,
                body: jsonBody,
                headers: { ...getHeaders(), ...fetchOptions.headers },
            });
        } catch (e) {
            // This only happens on network failure/timeout/etc
            throw new FetcherError("Network failure", {
                status: -1,
                error: e as Error,
                publicMessage: "Network error",
            });
        }

        // Now that we have a valid response, attempt to parse the body as text first
        const text = await response.text();

        if (!response.ok) {
            // If it's an invalid response (status > 399), try to parse out the APIError
            const apiError = this.parseApiError(text);

            throw new FetcherError(`Request failed ${response.status}`, {
                status: response.status,
                publicMessage: apiError?.err ?? "Request failed", // set the APIError message as what is displayed
                body: text,
                apiError,
            });
        }

        // If there's no body at all but a successful response we can return undefined here
        if (text.length === 0) return { status: response.status, data: undefined as T };

        try {
            // Finally, attempt to parse the JSON body and return it as the type specified
            const data = JSON.parse(text) as T;
            return {
                status: response.status,
                data,
            };
        } catch (e) {
            throw new FetcherError("Failed to parse JSON", {
                status: response.status,
                body: text,
                error: e as Error,
            });
        }
    }

    async request<T = any>(path: string, options: FetcherOptions = {}): Promise<T> {
        try {
            const res = await this.rawRequest<T>(path, options);
            return res.data;
        } catch (e) {
            if (e instanceof FetcherError) {
                // If it's our FetcherError, log it out for debugging but transform it into a regular Error
                // so the UI can just display error.message
                console.error(e);
                throw new Error(e.publicMessage);
            } else {
                // Otherwise just throw the error
                throw e;
            }
        }
    }

    /**
     *
     * Convenience methods for the most common request variants.
     * To do a more granular request request() and rawRequest() are also available
     *
     */

    get<T = any>(path: string, options?: FetcherOptions): Promise<T> {
        return this.request<T>(path, { ...options, method: "GET" });
    }

    post<T = any>(path: string, options?: FetcherOptions): Promise<T> {
        return this.request<T>(path, { ...options, method: "POST" });
    }

    patch<T = any>(path: string, options?: FetcherOptions): Promise<T> {
        return this.request<T>(path, { ...options, method: "PATCH" });
    }

    delete<T = any>(path: string, options?: FetcherOptions): Promise<T> {
        return this.request<T>(path, { ...options, method: "DELETE" });
    }
}

export default Fetcher;
